import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryPie } from 'victory';
import DashboardService from 'api/dashboard/DashboardService';
import { FleetBySegmentDashboardDto } from 'api/dashboard/dtos/FleetBySegmentDashboardDto';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './VehiclesBySegment.module.scss';

const COLOR_SCALE = ['#02c7c7', '#d4a832', '#7c5cbf', '#e8873d', '#e05a8a', '#038cb6'];

const VehiclesBySegment = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<FleetBySegmentDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<{ name: string; value: number; color: string; x: number; y: number } | null>(null);
	const [hiddenIndexes, setHiddenIndexes] = useState<Set<number>>(new Set());

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const result = await DashboardService.getFleetBySegment({});
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, []);

	if (!data && !isLoading) return null;

	if (!data?.items || data.items.length === 0) {
		return (
			<Box className={tableStyles.container} title={t('dashboard.vehicles_by_segment') as string} subTitleTooltip={t('dashboard.vehicles_by_segment_tooltip' as any) as string}>
				{isLoading
					? <div className={styles.centerAlign}><LoadingSpinner /></div>
					: <WidgetNoData />
				}
			</Box>
		);
	}

	const toggleIndex = (index: number) => {
		const visibleCount = data.items.length - hiddenIndexes.size;
		if (!hiddenIndexes.has(index) && visibleCount <= 1) return;
		setHiddenIndexes(prev => {
			const next = new Set(prev);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});
	};

	const visibleItems = data.items.filter((_, i) => !hiddenIndexes.has(i));
	const visibleColorScale = data.items.map((_, i) => COLOR_SCALE[i % COLOR_SCALE.length]).filter((_, i) => !hiddenIndexes.has(i));
	const colorScale = data.items.map((_, i) => COLOR_SCALE[i % COLOR_SCALE.length]);

	const totalCount = data.items.reduce((sum, item) => sum + item.count, 0);
	const visibleTotal = visibleItems.reduce((sum, item) => sum + item.count, 0);

	const legendItemCount = data.items.length;
	const estimatedLegendHeight = Math.max(48, Math.ceil(legendItemCount / 3) * 28);
	const legendSideWidth = 220;

	const bottomDonutSize = Math.min(width, Math.max(height - estimatedLegendHeight, 0));
	const sideDonutSize = Math.min(Math.max(width - legendSideWidth, 0), height);
	const isSideLayout = sideDonutSize > bottomDonutSize;

	const donutAreaWidth = isSideLayout ? width - legendSideWidth : width;
	const donutAreaHeight = isSideLayout ? height : Math.max(height - estimatedLegendHeight, 0);
	const donutSize = Math.min(donutAreaWidth, donutAreaHeight);
	const pieRadius = donutSize * 0.44;
	const pieInnerRadius = pieRadius * 0.7467;

	const goToVehiclesBySegment = (segmentId: string | undefined, segmentName: string | undefined) => {
		try {
			// inactives: false — dashboard agrega só viaturas ativas; clique mantém o critério.
			if (segmentId) {
				const raw = localStorage.getItem('VEHICLESFILTER');
				const existing = raw ? JSON.parse(raw) : {};
				const next = {
					...existing,
					segmentId,
					segmentName: segmentName ?? '',
					inactives: false,
					page: 1,
					scrollToVehicleId: undefined,
				};
				localStorage.setItem('VEHICLESFILTER', JSON.stringify(next));
			} else {
				localStorage.setItem('VEHICLESFILTER', JSON.stringify({ inactives: false }));
			}
		} catch {
			// ignore
		}
		window.open('/vehicles', '_blank');
	};

	// eslint-disable-next-line
	const pieEvents: any[] = [{
		target: 'data',
		eventHandlers: {
			onMouseEnter: (evt: any, targetProps: any) => {
				const datum = targetProps.datum;
				const idx = targetProps.index as number;
				setTooltip({ name: datum.segmentName || datum.x, value: datum.count ?? datum.y, color: visibleColorScale[idx], x: evt.clientX, y: evt.clientY });
				return [];
			},
			onMouseMove: (evt: any) => {
				setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
				return [];
			},
			onMouseLeave: () => { setTooltip(null); return []; },
			onClick: (_evt: any, targetProps: any) => {
				const idx = targetProps.index as number;
				const item = visibleItems[idx];
				if (item) {
					goToVehiclesBySegment(item.segmentId, item.segmentName);
				}
				return [];
			},
		},
	}];

	return (
		<Box className={tableStyles.container} title={t('dashboard.vehicles_by_segment') as string} subTitleTooltip={t('dashboard.vehicles_by_segment_tooltip' as any) as string}>
			<div className={`${styles.chartContainer} ${isSideLayout ? styles.chartContainerSide : ''}`} ref={containerRef}>
				{isLoading ? <div className={styles.centerAlign}><LoadingSpinner /></div> :
					width > 0 && height > 0 && (
						<>
							<div className={styles.donutWrapper} style={{ height: donutAreaHeight, width: donutAreaWidth }}>
								<svg width={donutAreaWidth} height={donutAreaHeight}>
									<VictoryPie
										standalone={false}
										colorScale={visibleColorScale}
										innerRadius={pieInnerRadius}
										radius={pieRadius}
										width={donutAreaWidth}
										height={donutAreaHeight}
										data={visibleItems}
										x="segmentName"
										y="count"
										labels={() => ''}
										events={pieEvents}
										style={{ data: { cursor: 'pointer', stroke: '#fff', strokeWidth: 1 }, labels: { display: 'none' } }}
									/>
								</svg>
								<div className={styles.centerLabel} style={{ maxWidth: pieInnerRadius * 1.6 }}>
									<div className={styles.centerLabelSub}>{t('dashboard.assets_count_label' as any)}</div>
									<div className={styles.centerLabelValue} style={{ fontSize: Math.max(pieInnerRadius * 0.28, 10) }}>{visibleTotal}</div>
								</div>
							</div>
							<div className={`${styles.htmlLegend} ${isSideLayout ? styles.htmlLegendSide : ''}`}>
								{data.items.map((item, i) => {
									const hidden = hiddenIndexes.has(i);
									const pct = totalCount > 0 ? Math.round((item.count / totalCount) * 100) : 0;
									return (
										<span
											key={i}
											className={`${styles.legendItem} ${hidden ? styles.legendItemHidden : ''}`}
											onClick={() => toggleIndex(i)}
										>
											<span className={styles.legendDot} style={{ backgroundColor: hidden ? '#d1d5db' : colorScale[i] }} />
											<span className={styles.legendName}>{item.segmentName}</span>
											<span className={styles.legendPct}>{pct}%</span>
										</span>
									);
								})}
							</div>
						</>
					)}
				{tooltip && (
					<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: tooltip.color }} />
							<span>{tooltip.name}</span>
						</div>
						<div className={styles.tooltipValue}>{tooltip.value}</div>
					</ChartTooltipPortal>
				)}
			</div>
		</Box>
	);
};

export default VehiclesBySegment;
